/**
 * Core Ludo Game Types
 *
 * All TypeScript types and interfaces for the Ludo game.
 * Board: cross-shaped with shared track (52 squares), 4 home bases,
 * 4 home stretches (6 squares each), and central home triangle.
 */

// ============================================================================
// Player & Game Configuration
// ============================================================================

export type PlayerColor = 'red' | 'blue' | 'green' | 'yellow';
export type LudoDifficulty = 'easy' | 'medium' | 'hard';
export type PlayerType = 'human' | 'ai';

// ============================================================================
// Token Types
// ============================================================================

/** Where a token currently resides */
export type TokenLocation = 'home_base' | 'track' | 'home_stretch' | 'home';

/** Position of a single token */
export interface TokenPosition {
  tokenIndex: number;           // 0–3
  location: TokenLocation;
  trackPosition?: number;       // 0–51 absolute position on shared track
  homeStretchPosition?: number; // 0–5 on home stretch
}

/** Identifies a specific token */
export interface TokenIdentifier {
  player: PlayerColor;
  tokenIndex: number;
}

/** Full info about a token including its owner */
export interface TokenInfo {
  player: PlayerColor;
  tokenIndex: number;
  position: TokenPosition;
}

/** Board position for rendering (row/col on the visual grid) */
export interface BoardPosition {
  row: number;
  col: number;
}

// ============================================================================
// Move Types
// ============================================================================

export type MoveAction = 'enter' | 'move' | 'skip';

export interface LudoMove {
  player: PlayerColor;
  tokenIndex: number;
  action: MoveAction;
  diceValue: number;
}

export interface MoveValidation {
  valid: boolean;
  reason?: 'not_your_turn' | 'invalid_token' | 'no_six_to_enter' | 'blocked_by_own_token' | 'exceeds_home' | 'token_already_home' | 'game_over';
}

export interface MoveResult {
  success: boolean;
  move: LudoMove;
  newPosition: TokenPosition;
  captured?: TokenInfo;
  reachedHome: boolean;
  extraTurn: boolean;
  tripleSix: boolean;
  turnSkipped: boolean;
  gameOver: boolean;
  winner?: PlayerColor;
  error?: string;
}

export interface MoveRecord {
  moveNumber: number;
  player: PlayerColor;
  diceValue: number;
  tokenIndex: number;
  action: MoveAction;
  fromPosition: TokenPosition;
  toPosition: TokenPosition;
  captured?: TokenInfo;
  reachedHome: boolean;
  extraTurn: boolean;
  tripleSix: boolean;
  timestamp: number;
}

// ============================================================================
// Dice
// ============================================================================

export interface DiceResult {
  value: number;
  player: PlayerColor;
  consecutiveSixes: number;
  isTripleSix: boolean;
}

// ============================================================================
// Game State
// ============================================================================

export type LudoGameStatus =
  | { type: 'active' }
  | { type: 'win'; winner: PlayerColor }
  | { type: 'resignation'; winner: PlayerColor; resigned: PlayerColor };

export interface LudoGameState {
  tokens: Record<PlayerColor, TokenPosition[]>;
  playerCount: number;
  activePlayers: PlayerColor[];
  playerTypes: Record<PlayerColor, PlayerType>;
  currentTurn: PlayerColor;
  consecutiveSixes: number;
  lastDiceValue: number | null;
  awaitingMove: boolean;
  tokensHome: Record<PlayerColor, number>;
  moveHistory: MoveRecord[];
  gameStatus: LudoGameStatus;
  gameId: string;
  startedAt: number;
  lastMoveAt: number;
}

// ============================================================================
// Game Result & Statistics
// ============================================================================

export interface LudoGameResult {
  gameId: string;
  mode: LudoGameSettings['mode'];
  difficulty?: LudoDifficulty;
  playerCount: number;
  winner?: PlayerColor;
  reason: 'all_tokens_home' | 'resignation' | 'last_player_standing';
  totalMoves: number;
  capturesMade: number;
  sixesRolled: number;
  duration: number;
  completedAt: number;
}

export interface LudoGameStats {
  wins: number;
  losses: number;
  winPercentage: number;
  averageGameLength: number;
  totalCaptures: number;
}

export interface LudoStatistics {
  userId?: string;
  singlePlayer: {
    easy: LudoGameStats;
    medium: LudoGameStats;
    hard: LudoGameStats;
  };
  localMultiplayer: LudoGameStats;
  onlineMultiplayer: LudoGameStats;
  totalGames: number;
  totalPlayTime: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface LudoGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: LudoDifficulty;
  playerCount: number;        // 2–4
  aiOpponentCount?: number;   // 1–3 (for single-player)
  isPrivate?: boolean;
}

// ============================================================================
// Component Props
// ============================================================================

export interface LudoGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface LudoGameModeSelectorProps {
  onStartGame: (settings: LudoGameSettings) => void;
  onJoinPrivateGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

// ============================================================================
// Animation & Multiplayer
// ============================================================================

export interface TokenAnimation {
  player: PlayerColor;
  tokenIndex: number;
  fromPosition: BoardPosition;
  toPosition: BoardPosition;
  type: 'move' | 'enter' | 'capture' | 'home';
}

export interface TurnSkipInfo {
  player: PlayerColor;
  reason: 'no_valid_moves' | 'triple_six';
  nextTurn: PlayerColor;
}

// ============================================================================
// Constants
// ============================================================================

export const TRACK_LENGTH = 52;
export const HOME_STRETCH_LENGTH = 6;
export const MAX_CONSECUTIVE_SIXES = 3;
export const TOKENS_PER_PLAYER = 4;

export const PLAYER_COLORS: PlayerColor[] = ['red', 'blue', 'green', 'yellow'];

/** Starting positions on the shared track (absolute positions 0–51) */
export const PLAYER_START_POSITIONS: Record<PlayerColor, number> = {
  red: 0,
  blue: 13,
  green: 26,
  yellow: 39,
};

/** Home stretch entry points — the last track position before entering home stretch */
export const HOME_STRETCH_ENTRY: Record<PlayerColor, number> = {
  red: 50,
  blue: 11,
  green: 24,
  yellow: 37,
};

/** Safe spot positions on the shared track (absolute) — 4 start squares + 4 additional */
export const SAFE_SPOTS: number[] = [0, 8, 13, 21, 26, 34, 39, 47];
